import { FunctionComponent, HTMLAttributeAnchorTarget, useState } from 'react';
import './styles/IconButton.scss';

import {
    IconButton as MuiIconButton,
    IconButtonProps,
    Tooltip,
    Menu as MuiMenu,
    MenuItem as MuiMenuItem,
    Divider,
    ListItemIcon,
    ListItemText,
    Typography,
    Badge
} from '@mui/material';

import { ProgressSpinner } from './ProgressSpinner';
import { IMenuItem, TColor } from '@dw/models/shared';
import { classNames, componentClassName } from '@dw/utils';

interface IIconButtonProps extends IconButtonProps {
    loading?: boolean;
    loadingSpinnerSize?: number;

    icon: React.ReactNode;
    href?: string;
    target?: HTMLAttributeAnchorTarget;
    tooltip?: string;
    menuOptions?: IMenuItem[];
    onMenuOptionSelected?: (option: IMenuItem) => void;
    badgeContent?: React.ReactNode;
    badgeColor?: TColor;
}
export const IconButton: FunctionComponent<IIconButtonProps> = (props: IIconButtonProps) => {
    const {
        loading,
        icon,
        tooltip,
        menuOptions,
        onMenuOptionSelected,
        onClick,
        loadingSpinnerSize,
        badgeContent,
        badgeColor,
        ...baseButtonProps
    } = props;

    const [menuAnchorElement, setMenuAnchorElement] = useState<HTMLElement | null>(null);

    const cn = componentClassName('icon-button');

    const iconToRender = loading
        ? (<ProgressSpinner size={loadingSpinnerSize ?? 20} />)
        : badgeContent
            ? (
                <Badge badgeContent={badgeContent} color={badgeColor} max={99} className={cn('badge-wrapper')} slotProps={{
                    badge: {
                    className: cn('badge-wrapper', 'badge')
                    }
                }}>
                    {icon}
                </Badge>
            ) : icon;

    const button = (
        <MuiIconButton
            {...baseButtonProps}
            className={classNames(cn(), baseButtonProps.className)}
            disabled={props.disabled || loading}
            aria-controls={menuAnchorElement ? 'basic-menu' : undefined}
            aria-haspopup={menuOptions && menuOptions.length > 0}
            aria-expanded={menuAnchorElement ? 'true' : undefined}
            onClick={(e) => {
                if (!!menuOptions && menuOptions?.length > 0) {
                    setMenuAnchorElement(e.currentTarget);
                }
                
                if (onClick) {
                    onClick(e);
                }
            }}
        >
            {iconToRender}
        </MuiIconButton>
    )

    const buttonMenu = !!menuOptions && menuOptions.length > 0 ? (
        <MuiMenu
            anchorEl={menuAnchorElement}
            open={!!menuAnchorElement}
            onClose={() => setMenuAnchorElement(null)}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
            sx={{maxHeight: '1000px'}}
        >
            {
                menuOptions.filter(x => !x.hidden).map((x, index) => x.isDivider ? (
                    <Divider key={index} />
                ) : (
                    <MuiMenuItem
                        key={index}
                        onClick={() => {
                            setMenuAnchorElement(null);
                            if (onMenuOptionSelected) {
                                onMenuOptionSelected(x)
                            }
                        }}
                        disabled={x.disabled}
                    >
                        {x.icon && (
                            <ListItemIcon>
                                {x.icon}
                            </ListItemIcon>
                        )}
                        {x.content ?? (
                            <ListItemText disableTypography>
                                <Typography variant='body2' maxWidth='200px' whiteSpace='normal' color={x.color}>
                                    {x.text}
                                </Typography>
                            </ListItemText>
                        )}
                    </MuiMenuItem>
                ))
            }
        </MuiMenu>
    ) : null;

    // MUI puts `pointer-events: none` on `.Mui-disabled`, so a Tooltip wrapped directly around a
    // disabled button never receives the mouseenter that opens it - and a native `title` does not
    // fire on a disabled button either. A plain <span> around it DOES receive the event, which is
    // MUI's own documented workaround. Added only when it is actually needed, so an enabled button
    // renders exactly the tree it always did.
    const isDisabled = !!(props.disabled || loading);

    if (tooltip) {
        return (
            <>
                <Tooltip title={tooltip}>
                    {isDisabled ? (
                        <span className={cn('disabled-tooltip-wrapper')}>
                            {button}
                        </span>
                    ) : button}
                </Tooltip>
                {buttonMenu}
            </>
        )
    }

    return (
        <>
            {button}
            {buttonMenu}
        </>
    );
};